<template>
    <div v-if="active" class="camera-overlay">
        <video ref="video" autoplay playsinline muted></video>

        <!-- flash is always there -->
        <div ref="flashEl" class="flash"></div>

        <button class="shutter" @click="capture"></button>
        <button class="close" @click="stop">×</button>
    </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'

defineOptions({ name: 'CameraOverlay' })

const props = defineProps({
    active: { type: Boolean, default: false }
})
const emit = defineEmits(['captured', 'closed'])

const video = ref(null)
const flashEl = ref(null)
let stream = null

watch(() => props.active, async (val) => {
    if (val) {
        await start()
    } else {
        stop()
    }
})

onBeforeUnmount(() => stop())

const start = async () => {
    try {
        stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'environment' },
            audio: false
        })
        if (video.value) video.value.srcObject = stream
    } catch (err) {
        console.error('Camera error:', err)
    }
}

const stop = () => {
    if (stream) {
        stream.getTracks().forEach(t => t.stop())
        stream = null
    }
    emit('closed')
}

const capture = () => {
    if (!video.value) return

    // restart flash animation
    const el = flashEl.value
    el.classList.remove('active')
    void el.offsetWidth // force reflow
    el.classList.add('active')

    // snapshot
    const canvas = document.createElement('canvas')
    canvas.width = video.value.videoWidth
    canvas.height = video.value.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video.value, 0, 0)
    canvas.toBlob(blob => {
        if (blob) emit('captured', blob)
    }, 'image/jpeg', 0.9)
}
</script>

<style scoped>
.camera-overlay {
    position: fixed;
    inset: 0;
    background: black;
    display: flex;
    justify-content: center;
    align-items: center;
}
video {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.flash {
    position: absolute;
    inset: 0;
    background: white;
    opacity: 0;
    pointer-events: none;
}
.flash.active {
    animation: flashAnim 0.25s ease-out forwards;
}
@keyframes flashAnim {
    from { opacity: 1; }
    to { opacity: 0; }
}

.shutter {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: 4px solid white;
    background: rgba(255,255,255,0.3);
}
.close {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 32px;
    background: transparent;
    border: none;
    color: white;
}
</style>
